import {isRefractoryDeleted} from '@domain/refractory-product-catalog'
import type {ControlledAutocompleteSelectOption} from '@hm/fundamental'
import {useMemo} from 'react'

import {useRefractoriesQuery} from './useRefractoriesQuery'

import {useRefractoryCustomizationsQuery} from '@refra/modules/common/hooks/queries/customizations'
import type {Refractory} from '@refra/modules/refra-catalog/types'

interface UseRefractoriesOptionsParams {
  currentManufacturerId?: number
  refractoriesSearch?: string
  plantUpmId?: string
  activeAfter?: string
  ids?: number[]
  enabled?: boolean
}

interface UseRefractoriesOptionsResult {
  refractoriesOptions: ControlledAutocompleteSelectOption[]
  refractoriesMap: Record<number, Refractory>
  areRefractoriesFetching: boolean
}

export const useRefractoriesOptions = ({
  currentManufacturerId,
  refractoriesSearch,
  plantUpmId,
  activeAfter,
  ids,
  enabled = true
}: UseRefractoriesOptionsParams = {}): UseRefractoriesOptionsResult => {
  const useCustomizations = Boolean(plantUpmId)
  const queryParams = {
    name: refractoriesSearch ? refractoriesSearch : undefined,
    manufacturerIds: currentManufacturerId ? [currentManufacturerId] : undefined,
    activeAfter,
    ids
  }
  const customizationsQuery = useRefractoryCustomizationsQuery({
    ...queryParams,
    plantUpmId: plantUpmId ?? '',
    enabled: enabled && useCustomizations
  })
  const refractoriesQuery = useRefractoriesQuery({
    ...queryParams,
    enabled: enabled && !useCustomizations
  })
  const {data: refractories, isFetching: areRefractoriesFetching} = useCustomizations
    ? customizationsQuery
    : refractoriesQuery

  const refractoriesMap: Record<number, Refractory> = useMemo(
    () =>
      refractories?.refractories.reduce<Record<number, Refractory>>((map, refractory) => {
        map[refractory.id] = refractory
        return map
      }, {}) ?? {},
    [refractories?.refractories]
  )

  const refractoriesOptions: ControlledAutocompleteSelectOption[] = useMemo(
    () =>
      refractories?.refractories
        .filter((refractory) => !isRefractoryDeleted(refractory))
        .map((refractory) => ({
          label: refractory.name,
          value: refractory.id
        })) ?? [],
    [refractories?.refractories]
  )

  return {refractoriesOptions, refractoriesMap, areRefractoriesFetching}
}
